import React from "react";
import { Box, Typography } from "@mui/material";
import { keyframes } from "@emotion/react";

const floatingBounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-3px); }
`;

const GRADIENTS = [
  "linear-gradient(135deg, #534AB7, #8b7fe8)",
  "linear-gradient(135deg, #1D9E75, #5DCAA5)",
  "linear-gradient(135deg, #D85A30, #F0997B)",
  "linear-gradient(135deg, #BA7517, #EF9F27)",
  "linear-gradient(135deg, #185FA5, #378ADD)",
];

const PERIOD_LABEL = {
  daily: "Today",
  weekly: "This week",
  monthly: "This month",
  custom: "Custom range",
};

const MEDALS = ["🥇", "🥈", "🥉", "4th", "5th"];

function getInitials(name) {
  if (!name) return "?";
  return name.split(" ").filter(Boolean).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
}

const TopPerformersCard = ({ leaderboard = [], period = "daily", onUserClick }) => (
  <Box sx={{ backgroundColor: "#ffffff", border: "1px solid hsl(140 8% 88%)", borderRadius: "0.75rem", overflow: "hidden" }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: "20px", pt: "16px", pb: "14px", borderBottom: "1px solid hsl(140 8% 88%)" }}>
      <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
        🏆 Top performers
      </Typography>
      <Typography sx={{ fontSize: 10, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
        {PERIOD_LABEL[period]}
      </Typography>
    </Box>

    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", px: "16px", py: "16px" }}>
      {leaderboard.length === 0 ? (
        <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)", textAlign: "center", py: 3 }}>
          No data yet
        </Typography>
      ) : leaderboard.map((entry, i) => {
        const isFirst = i === 0;
        const gradient = GRADIENTS[i % GRADIENTS.length];
        const clickable = Boolean(onUserClick);

        return (
          <Box
            key={entry.internal_user?.id ?? i}
            onClick={clickable ? () => onUserClick(entry.internal_user?.id) : undefined}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              px: "12px",
              py: "10px",
              borderRadius: "8px",
              cursor: clickable ? "pointer" : "default",
              transition: "background-color 150ms ease",
              backgroundColor: isFirst ? "hsla(38, 92%, 50%, 0.05)" : "hsl(140 10% 95%)",
              border: isFirst ? "1px solid hsla(38, 92%, 50%, 0.2)" : "1px solid hsl(140 8% 88%)",
              "&:hover": clickable ? { backgroundColor: isFirst ? "hsla(38, 92%, 50%, 0.09)" : "hsl(140 8% 90%)" } : {},
            }}
          >
            <Box sx={{ width: 28, textAlign: "center", flexShrink: 0 }}>
              {i < 3 ? (
                <Typography sx={{ fontSize: 18, lineHeight: 1 }}>{MEDALS[i]}</Typography>
              ) : (
                <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>{MEDALS[i]}</Typography>
              )}
            </Box>

            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: gradient,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                animation: `${floatingBounce} 2.4s ease-in-out infinite`,
                animationDelay: `${i * 0.4}s`,
              }}
            >
              <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "#fff", lineHeight: 1 }}>
                {getInitials(entry.internal_user?.full_name)}
              </Typography>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }} noWrap>
                {entry.internal_user?.full_name}
              </Typography>
              <Typography sx={{ fontSize: 10, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)", mt: "2px" }}>
                {entry.signed_up_count ?? 0} sign-ups · {entry.applied_count ?? 0} applied
              </Typography>
            </Box>

            <Box sx={{ textAlign: "right", flexShrink: 0 }}>
              <Typography sx={{ fontSize: 20, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)", lineHeight: 1 }}>
                {entry.conversion_count}
              </Typography>
              <Typography sx={{ fontSize: 9, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", textTransform: "uppercase", color: "hsl(150 10% 46%)", letterSpacing: "0.04em" }}>
                conversions
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  </Box>
);

export default TopPerformersCard;
